import { useEffect, useState } from 'react';
import { Button } from '@plick/ui';
import { IconExternalLink, IconX } from '@tabler/icons-react';
import { Announcement } from '@interfaces/general';
import { plickTheme } from '@theme/index';
import { useSurveyShownOnce } from '@utils/analytics/useSurveyShownOnce';
import { AnnouncementOptionButtons } from '../shared/AnnouncementOptionButtons';
import { useRespondAnnouncement } from '../CommunicationsModal/hooks/useRespondAnnouncement';

interface Props {
  announcement: Announcement;
  /** Usuario clickeó la ✕ → descarte permanente (localStorage). */
  onDismiss: () => void;
  /** Usuario respondió una opción → ocultar solo en la sesión actual. */
  onRespond: () => void;
}

export const CommunicationsBanner = ({ announcement, onDismiss, onRespond }: Props) => {
  // Instancia local del hook → isSubmitting solo afecta a este banner.
  const { respond, isSubmitting, submittingOptionId } = useRespondAnnouncement('banner');

  // MEAS-1: banner CON opciones renderizado → survey_shown (1 vez por comercio+encuesta).
  useSurveyShownOnce(announcement.uuid, 'banner', Boolean(announcement.options?.length));

  const bgColor = announcement.bg_color || plickTheme.colors.secondaryLight;
  const textColor = announcement.text_color || plickTheme.colors.dark;

  // delay_before_close (segundos): habilita la X tras N segundos
  const initialDelay = announcement.delay_before_close ?? 0;
  const [canClose, setCanClose] = useState(initialDelay === 0);

  useEffect(() => {
    if (initialDelay > 0) {
      const t = setTimeout(() => setCanClose(true), initialDelay * 1000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [initialDelay]);

  // Persistente: la X solo aparece si ya respondió (el contrato "no cerrar
  // sin responder" se cumplio). Si reaparece tras el response, debe poder
  // descartarse manualmente.
  const isPersistent = announcement.is_persistent === true;
  const hasResponded = Boolean(announcement.user_response);
  const showCloseButton = (!isPersistent || hasResponded) && canClose;

  // Fallback CTA: si el backend manda `url` top-level y `options` vacio,
  // renderizamos un link que abre la URL en pestana nueva.
  const hasMainUrl = Boolean(announcement.url) && !announcement.options?.length;

  return (
    <div
      role="region"
      aria-label={announcement.title}
      style={{ backgroundColor: bgColor, color: textColor }}
      className="w-full px-4 py-2.5 flex items-center gap-3 shadow-sm"
    >
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{announcement.title}</p>
          {(announcement.subtitle || announcement.text) && (
            <p className="text-xs opacity-90 truncate">{announcement.subtitle || announcement.text}</p>
          )}
        </div>

        {announcement.options && announcement.options.length > 0 && (
          <div className="mt-1 sm:mt-0 sm:ml-auto">
            <AnnouncementOptionButtons
              options={announcement.options}
              announcementId={announcement.uuid}
              userResponse={announcement.user_response}
              bgColor={bgColor}
              textColor={textColor}
              isSubmitting={isSubmitting}
              submittingOptionId={submittingOptionId}
              isPersistent={announcement.is_persistent === true}
              variant="banner"
              respond={respond}
              onComplete={onRespond}
            />
          </div>
        )}

        {hasMainUrl && (
          <div className="mt-1 sm:mt-0 sm:ml-auto [&>button]:h-8 [&>button]:px-3 [&>button]:text-xs">
            <Button.Primary
              text="Más información"
              leftIcon={<IconExternalLink size={14} />}
              onClick={() => {
                // onRespond despues del open para que un banner is_persistent
                // con url top-level y options=[] no quede atrapado: la X esta
                // oculta (showCloseButton requiere hasResponded) y sin esto
                // no habria forma de dismissar.
                window.open(announcement.url ?? '#', '_blank', 'noopener,noreferrer');
                onRespond();
              }}
            />
          </div>
        )}
      </div>

      {showCloseButton && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar anuncio"
          className="flex-shrink-0 p-1 rounded hover:opacity-80 transition-opacity"
          style={{ color: textColor }}
        >
          <IconX size={18} />
        </button>
      )}
    </div>
  );
};
